import React, { useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { CalendarEventDto } from '@/types/calendar';
import { getPmClientEventEnergy, PmEventEnergy as PmEventEnergyData } from '@services/pm-client-calendar.service.ts';

// Енергія події для продакт-менеджера (2026-10-10): прихід (їжа) / витрата (тренування) і як саме її порахувала
// система. Головне - відповідь бекенду (ті самі формули, що й денний баланс: бібліотека / MET / готові AI-totals).
// Для AI-подій додатково - розклад по компонентах з `details`, з якого вийшли totals. Формул, яких немає в системі,
// тут не вигадуємо.

const round = (v: number) => Math.round(v * 10) / 10;
const fmt = (v: unknown) => (typeof v === 'number' && !Number.isNaN(v) ? String(round(v)) : v == null ? '—' : String(v));

const DIRECTION: Record<PmEventEnergyData['direction'], { label: string; color: 'success' | 'warning' | 'default' }> = {
  intake: { label: 'Прихід енергії', color: 'success' },
  burn: { label: 'Витрата енергії', color: 'warning' },
  none: { label: 'Не впливає на баланс', color: 'default' },
};

const METHOD: Record<PmEventEnergyData['method'], string> = {
  library_food: 'Розраховано системою з продукту бібліотеки',
  library_workout_met: 'Розраховано системою за MET тренування з бібліотеки',
  ai_totals: 'Готові підсумки, розраховані під час AI-обробки',
  none: 'Не рахується',
};

const FOOD_NUTRIENTS = [
  { key: 'kcal', label: 'Ккал', per100: 'caloriesPer100' },
  { key: 'protein', label: 'Білки', per100: 'proteinPer100' },
  { key: 'fat', label: 'Жири', per100: 'fatPer100' },
  { key: 'carbs', label: 'Вуглеводи', per100: 'carbsPer100' },
] as const;

const Formula: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{children}</Box>
);

// Формула системи з вхідними величинами з бекенду.
const SystemFormula: React.FC<{ energy: PmEventEnergyData }> = ({ energy }) => {
  const i = energy.inputs ?? {};
  if (energy.method === 'library_food') {
    const grams = i.qtyG != null ? `${fmt(i.qtyG)} г (qtyG)` : i.qty != null ? `${fmt(i.qty)} порц. × ${fmt(i.gramsPerUnit)} г (gramsPerUnit)` : '—';
    return (
      <Formula>
        {`Грами: ${grams} = ${fmt(i.totalGrams)} г\n`}
        {`Ккал: ${fmt(i.per100g?.kcal)} на 100 г × ${fmt(i.totalGrams)} / 100 = ${fmt(energy.kcal)}\n`}
        {`Б/Ж/В на 100 г: ${fmt(i.per100g?.protein)} / ${fmt(i.per100g?.fat)} / ${fmt(i.per100g?.carbs)}  →  ${fmt(energy.macros?.protein)} / ${fmt(energy.macros?.fat)} / ${fmt(energy.macros?.carbs)}`}
      </Formula>
    );
  }
  if (energy.method === 'library_workout_met') {
    return (
      <Formula>
        {`MET ${fmt(i.metValue)} × вага ${fmt(i.weightKg)} кг × ${fmt(i.durationMinutes)} хв / 60 = ${fmt(energy.kcal)} ккал`}
      </Formula>
    );
  }
  if (energy.method === 'ai_totals') {
    return <Formula>{`details.totals: ${JSON.stringify(i.totals)}`}</Formula>;
  }
  return null;
};

// Розклад AI-подій по компонентах: як із даних у details вийшли totals.
const AiBreakdown: React.FC<{ event: CalendarEventDto }> = ({ event }) => {
  const d = event.details;
  if (!d || event.isStructured) return null;
  const items: any[] = Array.isArray(d.components) ? d.components : d.totalAmount !== undefined ? [{ ...d, name: event.title }] : [];
  if (items.length === 0) return null;

  if (event.eventType.code === 'food') {
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Компонент</TableCell>
            <TableCell align="right">Кількість</TableCell>
            {FOOD_NUTRIENTS.map((n) => <TableCell key={n.key} align="right">{n.label}: на 100 → разом</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((c, idx) => (
            <TableRow key={idx}>
              <TableCell>{c.name ?? '—'}</TableCell>
              <TableCell align="right">{c.totalAmount != null ? `${fmt(c.totalAmount)} ${c.unit ?? ''}` : '—'}</TableCell>
              {FOOD_NUTRIENTS.map((n) => {
                const per100 = c.nutrition?.[n.per100];
                const total = per100 != null && c.totalAmount != null ? (per100 * c.totalAmount) / 100 : null;
                return <TableCell key={n.key} align="right">{per100 == null ? 'не розраховано' : `${fmt(per100)} → ${fmt(total)}`}</TableCell>;
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  if (event.eventType.code === 'workout') {
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Компонент</TableCell>
            <TableCell align="right">Кількість</TableCell>
            <TableCell align="right">Ккал за одиницю</TableCell>
            <TableCell align="right">MET</TableCell>
            <TableCell align="right">Ккал</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((c, idx) => {
            const perUnit = c.caloriesBurn?.perUnit;
            const total = perUnit != null && c.totalAmount != null ? perUnit * c.totalAmount : null;
            return (
              <TableRow key={idx}>
                <TableCell>{c.name ?? '—'}{c.intensity ? ` (${c.intensity})` : ''}</TableCell>
                <TableCell align="right">{c.totalAmount != null ? `${fmt(c.totalAmount)} ${c.unit ?? ''}` : '—'}</TableCell>
                <TableCell align="right">{perUnit == null ? 'не розраховано' : fmt(perUnit)}</TableCell>
                <TableCell align="right">{fmt(c.caloriesBurn?.metValue)}</TableCell>
                <TableCell align="right">{fmt(total)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  }
  return null;
};

export const PmEventEnergy: React.FC<{ clientId: number; event: CalendarEventDto }> = ({ clientId, event }) => {
  const [energy, setEnergy] = useState<PmEventEnergyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    setEnergy(null);
    getPmClientEventEnergy(clientId, event.id)
      .then(setEnergy)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [clientId, event.id]);

  if (loading) return <Box sx={{ py: 1 }}><CircularProgress size={20} /></Box>;
  if (error || !energy) return <Alert severity="warning" variant="outlined">Не вдалося отримати розрахунок енергії.</Alert>;

  const direction = DIRECTION[energy.direction];
  const isAiWorkout = event.eventType.code === 'workout' && !event.isStructured;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip size="small" label={direction.label} color={direction.color} />
        <Typography variant="h6" component="span">
          {energy.kcal == null ? '—' : `${energy.direction === 'burn' ? '−' : energy.direction === 'intake' ? '+' : ''}${energy.kcal} ккал`}
        </Typography>
        <Typography variant="caption" color="text.secondary">у денний баланс</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">{METHOD[energy.method]}</Typography>

      <SystemFormula energy={energy} />

      {energy.note && <Alert severity="info" variant="outlined">{energy.note}</Alert>}

      {!event.isStructured && (
        <Box>
          <Typography variant="body2" fontWeight={600} gutterBottom>З чого склались підсумки (AI)</Typography>
          <AiBreakdown event={event} />
          {isAiWorkout && (
            <Typography variant="caption" color="text.secondary">
              Ккал = ккал за одиницю × кількість; компоненти без розрахунку в суму не входять. Якщо вправу знайдено в довіднику
              MET, ккал за одиницю = MET × вага клієнта / 60 (на момент розпізнавання).
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
